import { useState } from "react";
import { toast } from "sonner";
import { RotateCw, ShieldCheck, CircleDashed } from "lucide-react";
import { useApi, errMsg } from "../lib/api";
import { Tick } from "./Kyc";

const KINDS = [["AADHAAR", "Aadhaar", "UIDAI"], ["PAN", "PAN", "Income Tax"], ["ESIC", "ESIC", "ESIC"]];

export const KycBadges = ({ kyc }) => {
  const k = kyc || {};
  if (!KINDS.some(([key]) => k[key])) return <span className="text-xs text-slate-400" data-testid="kyc-badges-none">—</span>;
  return (
    <span className="inline-flex items-center gap-2" data-testid="kyc-badges">
      {KINDS.filter(([key]) => k[key]).map(([key, label]) => (
        <span key={key} className="inline-flex items-center gap-0.5 text-[11px] text-slate-300" title={`${label}: ${k[key].ok ? "verified" : "does not match"}`}>
          <Tick ok={!!k[key].ok} testid={`kyc-badge-${key.toLowerCase()}`} />{label}</span>))}
    </span>);
};

const Row = ({ kind, label, src, r, canRun, busy, onRun, onAccept, current }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 py-2.5 border-b border-[#1F2A3E]/60 last:border-0" data-testid={`kyc-row-${kind.toLowerCase()}`}>
    <div className="text-sm flex items-center gap-2">{r ? <Tick ok={!!r.ok} testid={`kyc-tick-${kind.toLowerCase()}`} /> : <CircleDashed size={14} className="text-slate-500" />}{label}
      <span className="text-[11px] text-slate-500">{r ? (!r.ok && r.number_ok && r.official_name && r.official_name.toUpperCase() === (current || "").toUpperCase() ? `Name updated to the official spelling · re-check to confirm` : `${r.ok ? "Verified" : r.number_ok ? "Name differs" : "Not found"} with ${src}${r.official_name ? ` · ${r.official_name}` : ""} · ${new Date(r.at).toLocaleDateString("en-IN")}`) : "Not checked"}</span></div>
    {canRun && <div className="flex items-center gap-3">
      {r && r.number_ok && !r.name_ok && r.official_name && r.official_name.toUpperCase() !== (current || "").toUpperCase() && <button onClick={onAccept} disabled={busy} className="text-xs text-amber-300 hover:underline disabled:opacity-50" data-testid={`kyc-accept-name-${kind.toLowerCase()}`}>Use official spelling</button>}
      <button onClick={onRun} disabled={busy} className="inline-flex items-center gap-1 text-xs text-[#00E599] hover:underline disabled:opacity-50" data-testid={`kyc-recheck-${kind.toLowerCase()}`}>
      <RotateCw size={12} className={busy ? "animate-spin" : ""} />{busy ? "Checking…" : r ? "Re-check" : "Check now"}</button></div>}
  </div>);

export const KycPanel = ({ w, admin, onDone }) => {
  const api = useApi();
  const [busy, setBusy] = useState(null);
  const run = async (kind) => {
    setBusy(kind);
    try { const r = (await api.post(`/kyc/recheck/${w.id}`, { kinds: [kind] })).data.results[kind]; toast[r?.ok ? "success" : "error"](r?.ok ? `${kind} verified` : `${kind} does not match`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  const accept = async (kind) => {
    const r = (w.kyc || {})[kind];
    if (!window.confirm(`Change this worker's name from "${w.worker_full_name}" to the official ${kind} spelling "${r.official_name}" and check again? The check is charged by Surepass.`)) return;
    setBusy(kind);
    try {
      const out = (await api.post(`/kyc/recheck/${w.id}/accept-name`, { kind })).data;
      if (!out.rechecked) toast.message(`Name changed to ${out.name}. ${out.message}`);
      else toast[out.ok ? "success" : "error"](out.ok ? `Name changed to ${out.name} and ${kind} verified` : `Name changed, but ${kind} still does not match`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  const k = w.kyc || {};
  return (
    <div className="c-surface p-6 c-rise" data-testid="kyc-panel">
      <div className="c-eyebrow mb-2 flex items-center gap-2"><ShieldCheck size={13} />Government ID checks</div>
      {KINDS.map(([kind, label, src]) => <Row key={kind} kind={kind} label={label} src={src} r={k[kind]} busy={busy === kind} onRun={() => run(kind)} onAccept={() => accept(kind)} current={w.worker_full_name}
        canRun={admin && ((kind === "PAN" && w.pan_masked) || (kind === "ESIC" && w.esic_ip_number))} />)}
      {admin && <p className="text-[11px] text-slate-500 mt-2">Aadhaar is checked with the worker's consent during Add Worker. Each PAN or ESIC check is charged by Surepass.</p>}
    </div>);
};
